import { useEffect, useRef, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar } from '@/components/Avatar';
import { MobileAvatar } from '@/components/MobileAvatar';
import { MessageList } from '@/components/MessageList';
import { InputBox } from '@/components/InputBox';
import { VoiceControls } from '@/components/VoiceControls';
import { Loader } from '@/components/Loader';
import { useChat } from '@/hooks/useChat';
import { useChatStore } from '@/store/chatStore';
import './App.css';

function App() {
  const { messages, isLoading, isSpeaking, sendMessage, stopSpeaking } = useChat();
  const { error, setError, clearMessages, editMessage } = useChatStore();
  const messageListRef = useRef<HTMLDivElement>(null);
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [isManualSpeaking, setIsManualSpeaking] = useState(false);

  // Simulate loading for avatar, API, and resources
  useEffect(() => {
    const loadResources = async () => {
      try {
        // Simulate loading time for 3D models, API connections, etc.
        await new Promise((resolve) => setTimeout(resolve, 4500));
        setIsAppLoading(false);
        // Slight delay before showing content for smooth transition
        setTimeout(() => setShowContent(true), 100);
      } catch (error) {
        console.error('Error loading resources:', error);
        setIsAppLoading(false);
        setTimeout(() => setShowContent(true), 100);
      }
    };

    loadResources();
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, setError]);

  // Handle message edit - remove messages after it and resend
  const handleEditMessage = async (messageId: string, newText: string) => {
    // Update the message and remove all messages after it
    editMessage(messageId, newText);
    // Send to AI without adding another user message (skip duplicate)
    await sendMessage(newText, true);
  };

  return (
    <>
      <AnimatePresence mode="wait">
        {isAppLoading && (
          <motion.div
            key="loader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
          >
            <Loader message="Initializing Genetrix..." />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showContent && (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
            className="app"
          >
            <div className="gradient-bg"></div>

            <div className="glass-container">
              <header className="app-header">
                <button className="back-button" onClick={clearMessages} title="New chat">
                  <PlusIcon className="w-5 h-5" />
                </button>
                <h1 className="chat-title">Talk To Aivo</h1>
                <VoiceControls />
              </header>

              <div className="chat-content-wrapper">
                <div className="avatar-sidebar">
                  <Avatar isSpeaking={isSpeaking || isManualSpeaking} />
                </div>

                <div className="chat-section">
                  {messages.length === 0 ? (
                    <div className="listening-screen">
                      <p className="listening-text">Start a conversation</p>
                    </div>
                  ) : (
                    <main className="chat-main">
                      <AnimatePresence>
                        {error && (
                          <motion.div
                            className="error-toast"
                            initial={{ opacity: 0, y: -20, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -20, scale: 0.95 }}
                            transition={{ duration: 0.3, ease: 'easeOut' }}
                          >
                            <span className="error-icon">⚠️</span>
                            <span className="error-message">{error}</span>
                            <button
                              className="error-close"
                              onClick={() => setError(null)}
                              aria-label="Dismiss error"
                            >
                              ✕
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <div className="message-list-wrapper" ref={messageListRef}>
                        <MessageList
                          messages={messages}
                          isLoading={isLoading}
                          onSpeakingChange={setIsManualSpeaking}
                          onEditMessage={handleEditMessage}
                        />
                      </div>
                    </main>
                  )}
                </div>
              </div>

              <div className="input-wrapper">
                <div className="input-with-avatar">
                  <div className="mobile-avatar-wrapper">
                    <MobileAvatar isSpeaking={isSpeaking || isManualSpeaking} />
                  </div>
                  <div className="input-box-wrapper">
                    <InputBox
                      onSendMessage={sendMessage}
                      isLoading={isLoading}
                      isSpeaking={isSpeaking}
                      onStopSpeaking={stopSpeaking}
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default App;
